import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Star,
  Globe,
  X
} from 'lucide-react';
import { BASE_URL } from '../config';

export default function FAQContent({ onBack, mode = 'create' }) {
  const { faqId } = useParams();
  
  // Initialize form data function
  const initializeFormData = (data = null) => {
    return {
      question: data?.question || '',
      answer: data?.answer || '',
      order: data?.order || 0,
      seo_keywords: data?.seo_keywords || '',
      is_active: data?.is_active !== undefined ? data.is_active : true,
      is_featured: data?.is_featured !== undefined ? data.is_featured : false,
      associated_pages: data?.associated_pages || ''
    };
  };
  
  const [formData, setFormData] = useState(() => initializeFormData());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const summernoteRef = useRef(null);

  // Helper to determine if form should be read-only
  const isReadOnly = mode === 'view';

  // Page choices for FAQ association
  const pageChoices = [
    { value: 'home', label: 'Home Page' },
    { value: 'features', label: 'Features Page' },
    { value: 'blog', label: 'Blog Page' },
    { value: 'ai-assistant', label: 'AI Assistant' },
    { value: 'book-doctor', label: 'book-doctor Page' },
  ];

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000); // Shorter timeout for toast
  };

  // Handle page selection
  const togglePageSelection = (pageValue) => {
    if (isReadOnly) return;
    
    const currentPages = formData.associated_pages ? formData.associated_pages.split(',').map(p => p.trim()).filter(p => p) : [];
    let updatedPages;
    
    if (currentPages.includes(pageValue)) {
      updatedPages = currentPages.filter(p => p !== pageValue);
    } else {
      updatedPages = [...currentPages, pageValue];
    }
    
    const pagesString = updatedPages.join(', ');
    setFormData({ ...formData, associated_pages: pagesString });
    setSelectedPages(updatedPages);
  };

  // Initialize Summernote
  const initializeSummernote = () => {
    if (summernoteRef.current && window.$) {
      // Destroy existing instance if it exists
      if (window.$(summernoteRef.current).hasClass('note-editor')) {
        window.$(summernoteRef.current).summernote('destroy');
      }
      
      window.$(summernoteRef.current).summernote({
        height: 200,
        placeholder: 'Enter the answer to this question. You can use rich text formatting.',
        fontNames: ['Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Helvetica Neue', 'Helvetica', 'Impact', 'Lucida Grande', 'Nunito', 'Tahoma', 'Times New Roman', 'Verdana'],
        fontNamesIgnoreCheck: ['Nunito'], // Force include Nunito even if not detected
        toolbar: [
          ['style', ['style']],
          ['font', ['bold', 'underline', 'clear']],
          ['fontname', ['fontname']],
          ['color', ['color']],
          ['para', ['ul', 'ol', 'paragraph']],
          ['table', ['table']],
          ['insert', ['link']],
          ['view', ['fullscreen', 'codeview', 'help']]
        ],
        callbacks: {
          onChange: function(contents) {
            if (!isReadOnly) {
              setFormData(prev => ({ ...prev, answer: contents }));
            }
          },
          onKeyup: function(e) {
            if (!isReadOnly) {
              const contents = window.$(summernoteRef.current).summernote('code');
              setFormData(prev => ({ ...prev, answer: contents }));
            }
          },
          onPaste: function(e) {
            if (!isReadOnly) {
              setTimeout(() => {
                const contents = window.$(summernoteRef.current).summernote('code');
                setFormData(prev => ({ ...prev, answer: contents }));
              }, 100);
            }
          }
        },
        disabled: isReadOnly
      });
      
      // Set initial content
      if (formData.answer) {
        window.$(summernoteRef.current).summernote('code', formData.answer);
      }
    }
  };

  // Destroy Summernote
  const destroySummernote = () => {
    if (summernoteRef.current && window.$) {
      window.$(summernoteRef.current).summernote('destroy');
    }
  };

  // Fetch FAQ content for edit/view mode
  const fetchFaqContent = async () => {
    console.log('🔍 fetchFaqContent called with:', { mode, faqId });
    
    if ((mode !== 'edit' && mode !== 'view') || !faqId) {
      console.log('❌ Not fetching - mode or faqId invalid');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      console.log('🔍 Token found:', token ? 'Yes' : 'No');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      // Ensure proper URL formatting
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const url = `${baseUrl}/api/seo/admin/faq/${faqId}/`;
      
      console.log('🔍 Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('📡 Fetch response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ FAQ data received:', data);
        setFormData(initializeFormData(data));
        
        // Initialize selected pages
        const pages = data.associated_pages ? data.associated_pages.split(',').map(p => p.trim()).filter(p => p) : [];
        setSelectedPages(pages);
      } else {
        const errorText = await response.text();
        console.error('❌ Fetch error response:', errorText);
        
        let errorMessage = 'Failed to fetch FAQ content';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('❌ Error fetching FAQ content:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showNotification('Network error: Unable to connect to server. Please check your internet connection.', 'error');
      } else {
        showNotification(`Error fetching FAQ content: ${error.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      console.log('Token found:', token ? 'Yes' : 'No');
      console.log('Token length:', token ? token.length : 0);
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        setSaving(false);
        return;
      }

      const method = mode === 'create' ? 'POST' : 'PUT';
      
      // Validate required fields
      // Get current content from Summernote editor
      let currentAnswer = formData.answer;
      if (summernoteRef.current && window.$ && window.$.fn.summernote) {
        currentAnswer = window.$(summernoteRef.current).summernote('code');
      }
      
      // For Summernote content, strip HTML tags and check for actual text content
      const stripHtml = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html || '';
        return tmp.textContent || tmp.innerText || '';
      };
      
      const answerText = stripHtml(currentAnswer).trim();
      
      console.log('Validation Debug:', {
        question: formData.question,
        questionLength: formData.question?.length,
        currentAnswer: currentAnswer,
        answerText: answerText,
        answerTextLength: answerText.length
      });
      
      if (!formData.question.trim() || !answerText) {
        showNotification('Please fill in all required fields (Question, Answer)', 'error');
        setSaving(false);
        return;
      }
      
      // Update formData with current Summernote content
      const updatedFormData = {
        ...formData,
        answer: currentAnswer
      };

      const submitData = {
        ...updatedFormData,
        order: parseInt(updatedFormData.order) || 0
      };

      // Ensure proper URL formatting
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      
      const url = mode === 'create' 
        ? `${baseUrl}/api/seo/admin/faq/create/`
        : `${baseUrl}/api/seo/admin/faq/${faqId}/`;
      
      console.log('Making request to:', url);
      console.log('Request method:', method);
      console.log('Request data:', submitData);
      console.log('Authorization header:', `Bearer ${token.substring(0, 20)}...`);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        showNotification(
          mode === 'create' ? 'FAQ item created successfully' : 'FAQ item updated successfully'
        );
        
        // Navigate back after a short delay
        setTimeout(() => {
          onBack();
        }, 1500);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorMessage = `Operation failed (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use the text as error message
          errorMessage = errorText || errorMessage;
        }
        
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Network error:', error);
      
      // More specific error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showNotification('Network error: Unable to connect to server. Please check your internet connection and server status.', 'error');
      } else if (error.name === 'AbortError') {
        showNotification('Request timeout. Please try again.', 'error');
      } else {
        showNotification(`Network error: ${error.message}. Please check your connection.`, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    // Initialize form data first to prevent controlled/uncontrolled input warnings
    if (Object.keys(formData).length === 0) {
      setFormData(initializeFormData());
    }
    
    // Then fetch FAQ content if in edit or view mode
    if ((mode === 'edit' || mode === 'view') && faqId) {
      fetchFaqContent();
    }
    
    // Initialize selected pages from form data
    const pages = formData.associated_pages ? formData.associated_pages.split(',').map(p => p.trim()).filter(p => p) : [];
    setSelectedPages(pages);
  }, [mode, faqId]);

  // Initialize Summernote after component mounts and when formData changes
  useEffect(() => {
    // Load Summernote if not already loaded
    if (!window.$ || !window.$.fn.summernote) {
      // Load jQuery and Summernote
      const jqueryScript = document.createElement('script');
      jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
      jqueryScript.onload = () => {
        const summernoteScript = document.createElement('script');
        summernoteScript.src = 'https://cdn.jsdelivr.net/npm/summernote@0.8.18/dist/summernote-lite.min.js';
        summernoteScript.onload = () => {
          // Load CSS
          const summernoteCSS = document.createElement('link');
          summernoteCSS.rel = 'stylesheet';
          summernoteCSS.href = 'https://cdn.jsdelivr.net/npm/summernote@0.8.18/dist/summernote-lite.min.css';
          document.head.appendChild(summernoteCSS);
          
          // Initialize after a short delay
          setTimeout(initializeSummernote, 100);
        };
        document.head.appendChild(summernoteScript);
      };
      document.head.appendChild(jqueryScript);
    } else {
      // Summernote already loaded, initialize directly
      setTimeout(initializeSummernote, 100);
    }

    // Cleanup on unmount
    return () => {
      destroySummernote();
    };
  }, [formData.answer, isReadOnly]);

  // Update Summernote content when formData.answer changes
  useEffect(() => {
    if (summernoteRef.current && window.$ && window.$.fn.summernote) {
      const currentContent = window.$(summernoteRef.current).summernote('code');
      if (currentContent !== formData.answer) {
        window.$(summernoteRef.current).summernote('code', formData.answer || '');
      }
    }
  }, [formData.answer]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          {/* Back to FAQ Management */}
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {mode === 'create' ? 'Create FAQ Item' : mode === 'edit' ? 'Edit FAQ Item' : 'View FAQ Item'}
          </h1>
          <p className="text-slate-600">
            {mode === 'create' ? 'Create a new frequently asked question' : mode === 'edit' ? 'Update FAQ item details' : 'View FAQ item details'}
          </p>
        </div>
      </div>

      {/* Toast Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.3 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.3 }}
          className={`fixed top-4 right-4 z-50 rounded-lg shadow-lg overflow-hidden min-w-[300px] max-w-[400px] ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
        >
          <div className="p-4 flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Progress bar */}
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: 3, ease: "linear" }}
            className={`h-1 ${
              notification.type === 'success' 
                ? 'bg-green-400' 
                : 'bg-red-400'
            }`}
          />
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-medical-100 rounded-lg flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-medical-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Basic Information</h2>
              <p className="text-sm text-slate-600">Essential FAQ information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Question * <span className="text-xs text-slate-500">({formData.question?.length || 0}/300 characters)</span>
              </label>
              <input
                type="text"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                readOnly={isReadOnly}
                maxLength={300}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                required
                placeholder="Enter your frequently asked question"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Answer * <span className="text-xs text-slate-500">(Rich Text Editor)</span>
              </label>
              {isReadOnly ? (
                <div 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 min-h-[200px]"
                  dangerouslySetInnerHTML={{ __html: formData.answer }}
                />
              ) : (
                <div>
                  <div ref={summernoteRef}></div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                  placeholder="0"
                />
                <p className="text-xs text-slate-500 mt-1">Lower numbers appear first</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  SEO Keywords
                </label>
                <input
                  type="text"
                  value={formData.seo_keywords}
                  onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                  placeholder="keyword1, keyword2, keyword3"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated keywords</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-medical-600 focus:ring-medical-500 border-slate-300 rounded disabled:opacity-50"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-slate-700">
                  Active
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-medical-600 focus:ring-medical-500 border-slate-300 rounded disabled:opacity-50"
                />
                <label htmlFor="is_featured" className="ml-2 block text-sm text-slate-700 flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Featured
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Page Association Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Page Association</h2>
              <p className="text-sm text-slate-600">Choose which pages this FAQ should appear on</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Associated Pages
              </label>
              <input
                type="text"
                value={formData.associated_pages}
                onChange={(e) => {
                  setFormData({ ...formData, associated_pages: e.target.value });
                  const pages = e.target.value ? e.target.value.split(',').map(p => p.trim()).filter(p => p) : [];
                  setSelectedPages(pages);
                }}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="home, pricing, contact (leave empty for all pages)"
              />
              <p className="text-xs text-slate-500 mt-1">
                {/* Comma-separated page keys. Leave empty to show on all pages. */}
              </p>
            </div>

            <div>
              {/* <p className="text-sm font-medium text-slate-700 mb-3">Available Pages (Click to select/deselect):</p> */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {pageChoices.map((page) => {
                  const isSelected = selectedPages.includes(page.value);
                  return (
                    <button
                      key={page.value}
                      type="button"
                      onClick={() => togglePageSelection(page.value)}
                      disabled={isReadOnly}
                      className={`text-left p-2 rounded-lg border transition-all duration-200 ${
                        isSelected
                          ? 'bg-medical-100 border-medical-300 text-medical-800'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono text-xs font-medium">{page.value}</span>
                          <div className="text-xs text-slate-500">{page.label}</div>
                        </div>
                        {isSelected && (
                          <div className="w-4 h-4 bg-medical-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {selectedPages.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-slate-600 mb-2">Selected pages:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPages.map((pageValue) => {
                      const page = pageChoices.find(p => p.value === pageValue);
                      return (
                        <span
                          key={pageValue}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-medical-100 text-medical-800 rounded-full text-xs"
                        >
                          {page ? page.label : pageValue}
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => togglePageSelection(pageValue)}
                              className="hover:bg-medical-200 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Submit Button - Hide in view mode */}
        {!isReadOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-end gap-3 pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-medical-500 text-white px-8 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {mode === 'create' ? 'Create FAQ Item' : 'Update FAQ Item'}
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Back Button for View Mode */}
        {isReadOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-end gap-3 pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="bg-slate-500 text-white px-8 py-2 rounded-lg hover:bg-slate-600 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {/* Back to FAQ Management */}
            </button>
          </motion.div>
        )}
      </form>
    </div>
  );
}